import { Fragment, useState } from "react";
import { Drawer, IconButton, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { X } from "lucide-react";
import dayjs from "dayjs";
import { useStateValue } from "../../context/StateValueContext";
import type { ItemHistoryAction, OnboardingDetail, OnboardingDetailItem, OnboardingHistoryEntry } from "../../types/onboarding";
import { HISTORY_ACTION_LABELS, ITEM_NETWORK_LABELS } from "./labels";
import { toActorName, withoutBrand } from "./checklistRows";

type Chip = "all" | "client" | "done";
type Change = { entry: OnboardingHistoryEntry; item: OnboardingDetailItem };
type Burst = { key: number; fold: string | null; changes: Change[] };

const CHIPS: { key: Chip; label: string; actions?: ItemHistoryAction[] }[] = [
  { key: "all", label: "All" },
  { key: "client", label: "Client", actions: ["asked", "sent", "chased"] },
  { key: "done", label: "Done", actions: ["done", "live", "not_needed"] },
];

const VERBS: Record<ItemHistoryAction, string> = {
  ...HISTORY_ACTION_LABELS,
  sent: "sent to the network",
  done: "marked Done",
  live: "marked Live",
  not_needed: "marked Not needed",
  reopened: "reopened",
  back_to_todo: "moved back to To do",
  added: "added a step",
};

// What a step became, in a folded row's counts and its list of steps.
const STATES: Record<ItemHistoryAction, string> = {
  asked: "Asked", sent: "Sent", chased: "Chased", done: "Done", live: "Live", not_needed: "Not needed", retired_by_edit: "Retired",
  reopened: "Reopened", reopened_by_edit: "Reopened", back_to_todo: "To do", notes: "Notes", details: "Details", added: "Added",
};

const ACTION_CLASSES: Partial<Record<ItemHistoryAction, string>> = {
  done: "text-success-600", live: "text-success-600", asked: "text-warning-600", sent: "text-warning-600", chased: "text-warning-600", not_needed: "text-gray-500",
};

const BURST_SECONDS = 60;
const LISTED_STEPS = 5;

const MARKS: ItemHistoryAction[] = ["done", "live", "not_needed", "back_to_todo", "reopened"];

// An answers edit writes its steps in one transaction, so they share the actor and the time to the
// millisecond. A staff-added step is never part of one.
const isEditMark = ({ entry, item }: Change) =>
  entry.action === "retired_by_edit" || entry.action === "reopened_by_edit" || (entry.action === "added" && item.itemKey !== "custom");

// Which row a change may share: an edit only with its own edit, staff marks with marks, an ask, chase or
// send with its own kind. Notes, details and added steps always stand alone.
function toFoldKey(change: Change): string | null {
  const { entry, item } = change;
  if (isEditMark(change)) return `edit:${entry.createdAt}`;
  if (MARKS.includes(entry.action)) return "marks";
  if (entry.action === "sent") return `sent:${item.network}`;
  if (entry.action === "asked" || entry.action === "chased") return entry.action;
  return null;
}

// One person's changes of one fold kind within a minute are one row, even with other changes in between.
function toBursts(changes: Change[]): Burst[] {
  const bursts: Burst[] = [];
  for (const change of changes) {
    const { entry } = change;
    const fold = toFoldKey(change);
    const open = fold && bursts.find((b) => b.fold === fold && b.changes[0].entry.actorName === entry.actorName
      && dayjs(b.changes[0].entry.createdAt).diff(entry.createdAt, "second") <= BURST_SECONDS);
    if (open) open.changes.push(change);
    else bursts.push({ key: entry.historyId, fold, changes: [change] });
  }
  return bursts;
}

function toDayLabel(day: string): string {
  const today = dayjs.utc().format("YYYY-MM-DD");
  if (day === today) return "Today";
  if (day === dayjs(today).subtract(1, "day").format("YYYY-MM-DD")) return "Yesterday";
  return dayjs(day).format("MMM D");
}

const toVerb = (action: ItemHistoryAction, items: OnboardingDetailItem[]) =>
  action === "sent" && items[0].network && items.every((i) => i.network === items[0].network) ? `sent to ${ITEM_NETWORK_LABELS[items[0].network]}` : VERBS[action];

function StepName({ item }: { item: OnboardingDetailItem }) {
  return <>{withoutBrand(item)}{item.brandName && <span className="text-gray-400"> · {item.brandName}</span>}</>;
}

// Counts per action in the order they first appear, newest first.
function toCounts(changes: Change[]): Array<[ItemHistoryAction, number]> {
  const counts = new Map<ItemHistoryAction, number>();
  for (const { entry } of changes) counts.set(entry.action, (counts.get(entry.action) ?? 0) + 1);
  return [...counts];
}

function HistoryRow({ actor, time, children, below }: { actor: string; time: string; children: React.ReactNode; below?: React.ReactNode }) {
  return (
    <li className="flex flex-col gap-1.5 border-b border-gray-50 py-[7px] last:border-b-0 dark:border-gray-800">
      <div className="grid grid-cols-[1fr_44px] gap-2">
        <div><span className="font-medium text-gray-800 dark:text-white/90">{actor}</span> {children}</div>
        <span className="text-right text-xs text-gray-400">{time}</span>
      </div>
      {below}
    </li>
  );
}

// A folded row of one kind reads on one line; mixed kinds put their counts on a second line, so the
// show link always ends the first.
function BurstRow({ burst, me }: { burst: Burst; me: string | undefined }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const { changes } = burst;
  const [{ entry, item }] = changes;
  const actor = toActorName(entry.actorName, entry.actorFirstName, me);
  const time = dayjs.utc(entry.createdAt).format("HH:mm");
  const edit = !!burst.fold?.startsWith("edit:");

  if (changes.length === 1) {
    return (
      <HistoryRow actor={actor} time={time}>
        {edit
          ? <>edited the answers · {STATES[entry.action]} · <StepName item={item} /></>
          : <><span className={ACTION_CLASSES[entry.action]}>{toVerb(entry.action, [item])}</span> · <StepName item={item} /></>}
      </HistoryRow>
    );
  }
  const counts = toCounts(changes);
  const oneKind = counts.length === 1;
  const toCount = ([action, n]: [ItemHistoryAction, number]) => `${n} ${edit ? STATES[action].toLowerCase() : STATES[action]}`;
  const toggle = (
    <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="cursor-pointer text-[13px] text-brand-500 hover:text-brand-600">
      {open ? "hide ▴" : "show ▾"}
    </button>
  );
  const listed = all ? changes : changes.slice(0, LISTED_STEPS);
  return (
    <HistoryRow
      actor={actor} time={time}
      below={(
        <>
          {!oneKind && (
            <div className="text-[13px] text-gray-500">
              {counts.map(([action, n], i) => (
                <Fragment key={action}>{i > 0 && " · "}<span className={edit ? undefined : ACTION_CLASSES[action]}>{toCount([action, n])}</span></Fragment>
              ))}
            </div>
          )}
          {open && (
            <div className="flex flex-col items-start gap-[3px] border-l-2 border-gray-100 pl-3 text-[13px] text-gray-500 dark:border-gray-800">
              <ul aria-label="Steps" className="flex flex-col gap-[3px]">
                {listed.map((c) => (
                  <li key={c.entry.historyId}><span className={ACTION_CLASSES[c.entry.action]}>{STATES[c.entry.action]}</span> · <StepName item={c.item} /></li>
                ))}
              </ul>
              {!all && changes.length > LISTED_STEPS && (
                <button type="button" onClick={() => setAll(true)} className="cursor-pointer text-brand-500 hover:text-brand-600">+ {changes.length - LISTED_STEPS} more</button>
              )}
            </div>
          )}
        </>
      )}
    >
      {edit ? <>edited the answers · <span className="whitespace-nowrap">{oneKind && `${toCount(counts[0])} · `}{toggle}</span></>
        : oneKind ? <><span className={ACTION_CLASSES[entry.action]}>{toVerb(entry.action, changes.map((c) => c.item))}</span> · <span className="whitespace-nowrap">{changes.length} steps · {toggle}</span></>
          : <>changed <span className="whitespace-nowrap">{changes.length} steps · {toggle}</span></>}
    </HistoryRow>
  );
}

export default function HistoryDrawer({ open, onClose, onboarding }: {
  open: boolean;
  onClose: () => void;
  onboarding: Pick<OnboardingDetail, "history" | "items" | "createdAt" | "createdByEmail" | "createdByFirstName">;
}) {
  const { history, items } = onboarding;
  const [{ user }] = useStateValue();
  const [chip, setChip] = useState<Chip>("all");
  const actions = CHIPS.find((c) => c.key === chip)?.actions;
  const changes = history.flatMap((entry) => {
    const item = items.find((i) => i.itemId === entry.itemId);
    return item && (!actions || actions.includes(entry.action)) ? [{ entry, item }] : [];
  });
  const days = new Map<string, Change[]>();
  for (const change of changes) {
    const day = dayjs.utc(change.entry.createdAt).format("YYYY-MM-DD");
    days.set(day, [...(days.get(day) ?? []), change]);
  }
  // The onboarding's start closes the full history; steps added later by staff or an edit aren't counted.
  const startDay = chip === "all" ? dayjs.utc(onboarding.createdAt).format("YYYY-MM-DD") : null;
  if (startDay && !days.has(startDay)) days.set(startDay, []);
  const startedSteps = items.filter((i) => !history.some((h) => h.itemId === i.itemId && h.action === "added")).length;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: "min(440px, 100vw)" } } }}>
      <section aria-label="History" className="flex flex-col gap-3 px-5 pb-5 pt-4 text-sm text-gray-700 dark:text-gray-300">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">History</h2>
          <IconButton size="small" aria-label="Close history" onClick={onClose}><X size={18} /></IconButton>
        </div>
        <ToggleButtonGroup exclusive size="small" aria-label="Show changes" value={chip} onChange={(_, value: Chip | null) => value && setChip(value)}>
          {CHIPS.map((c) => <ToggleButton key={c.key} value={c.key}>{c.label}</ToggleButton>)}
        </ToggleButtonGroup>
        {[...days].map(([day, dayChanges]) => (
          <div key={day} className="flex flex-col">
            <h3 className="pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">{toDayLabel(day)}</h3>
            <ul aria-label={toDayLabel(day)} className="flex flex-col">
              {toBursts(dayChanges).map((burst) => <BurstRow key={burst.key} burst={burst} me={user?.email} />)}
              {day === startDay && (
                <HistoryRow actor={toActorName(onboarding.createdByEmail, onboarding.createdByFirstName, user?.email)} time={dayjs.utc(onboarding.createdAt).format("HH:mm")}>
                  started the onboarding · {startedSteps} {startedSteps === 1 ? "step" : "steps"}
                </HistoryRow>
              )}
            </ul>
          </div>
        ))}
        {!days.size && <p className="py-6 text-center text-gray-500">No changes yet</p>}
      </section>
    </Drawer>
  );
}
